import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Plus, X, Shield, Award, ChevronDown, ChevronRight } from 'lucide-react';

const SKILL_CATEGORIES = [
  'Management', 'Strategy', 'Commercial Negotiations', 'Development', 'AI',
  'Hardware Engineering', 'Legal', 'Accounting', 'Forecasting', 'Marketing',
  'Sales', 'Product', 'Design', 'Data Analysis', 'Operations', 'Other'
];

const SKILL_LEVEL_NAMES = {
  1: 'Novice',
  2: 'Competent',
  3: 'Proficient',
  4: 'Expert',
  5: 'Master'
};

export default function SkillRequirementsManager({ requirements = [], rewards = [], onChange }) {
  const [reqSkill, setReqSkill] = useState('');
  const [reqLevel, setReqLevel] = useState(1);
  const [rewardSkill, setRewardSkill] = useState('');
  const [rewardSP, setRewardSP] = useState(10);

  const addRequirement = () => {
    if (!reqSkill) return;
    const newRequirements = [...requirements, { skill_name: reqSkill, minimum_level: reqLevel }];
    const newRewards = [...rewards, { skill_name: reqSkill, sp_amount: reqLevel }];
    onChange({
      requirements: newRequirements,
      rewards: newRewards
    });
    setReqSkill('');
    setReqLevel(1);
  };

  const removeRequirement = (index) => {
    const removedReq = requirements[index];
    onChange({
      requirements: requirements.filter((_, i) => i !== index),
      rewards: rewards.filter((r) => r.skill_name !== removedReq.skill_name)
    });
  };

  const addReward = () => {
    if (!rewardSkill || rewardSP <= 0) return;
    const matchingReq = requirements.find((r) => r.skill_name === rewardSkill);
    const maxSP = matchingReq ? matchingReq.minimum_level * 2 : 1000;
    const finalSP = Math.min(rewardSP, maxSP);
    onChange({
      requirements,
      rewards: [...rewards, { skill_name: rewardSkill, sp_amount: finalSP }]
    });
    setRewardSkill('');
    setRewardSP(10);
  };

  const removeReward = (index) => {
    onChange({
      requirements,
      rewards: rewards.filter((_, i) => i !== index)
    });
  };

  const updateReward = (index, newSP) => {
    const reward = rewards[index];
    const matchingReq = requirements.find((r) => r.skill_name === reward.skill_name);
    const maxSP = matchingReq ? matchingReq.minimum_level * 2 : 1000;
    const finalSP = Math.min(Math.max(1, parseInt(newSP) || 1), maxSP);
    const updatedRewards = [...rewards];
    updatedRewards[index] = { ...reward, sp_amount: finalSP };
    onChange({
      requirements,
      rewards: updatedRewards
    });
  };

  const [isOpen, setIsOpen] = useState(requirements.length > 0 || rewards.length > 0);

  const hasSummary = requirements.length > 0 || rewards.length > 0;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <button type="button" className="flex items-center justify-between w-full text-left">
          <div className="flex items-center gap-2">
            {isOpen ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
            <Shield className="w-4 h-4 text-indigo-400" />
            <Label className="text-sm font-semibold text-slate-300 cursor-pointer">Skill Requirements & Rewards</Label>
          </div>
          {!isOpen && hasSummary && (
            <div className="flex flex-wrap gap-1 max-w-[60%] justify-end">
              {requirements.map((req, idx) => (
                <Badge key={`req-${idx}`} variant="outline" className="text-[10px] text-red-400 border-red-400/30">
                  {req.skill_name} L{req.minimum_level}
                </Badge>
              ))}
              {rewards.filter(r => !requirements.some(req => req.skill_name === r.skill_name)).map((reward, idx) => (
                <Badge key={`rew-${idx}`} variant="outline" className="text-[10px] text-amber-400 border-amber-400/30">
                  {reward.skill_name} +{reward.sp_amount}SP
                </Badge>
              ))}
            </div>
          )}
          {!isOpen && !hasSummary && (
            <span className="text-xs text-slate-500 italic">None set</span>
          )}
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-4 space-y-6">
      {/* Skill Requirements */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Shield className="w-4 h-4 text-red-400" />
          <Label className="text-sm font-semibold text-slate-300">Skill Requirements (Minimum to be Responsible)</Label>
        </div>
        
        <div className="space-y-2">
          {requirements.map((req, idx) => (
            <div key={idx} className="flex items-center justify-between p-3 bg-slate-950 rounded-lg border border-slate-800">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-red-400 border-red-400/30">
                  {req.skill_name}
                </Badge>
                <span className="text-sm text-slate-400">Min Level: {req.minimum_level} ({SKILL_LEVEL_NAMES[req.minimum_level]})</span>
              </div>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                onClick={() => removeRequirement(idx)}
                className="h-8 w-8 text-slate-500 hover:text-red-400"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>

        <div className="p-3 bg-slate-950/50 rounded-lg border border-dashed border-slate-700 space-y-2">
          <div className="text-xs text-slate-500 font-medium">Select skill & level, then click "Add":</div>
          <div className="flex gap-2">
            <Select value={reqSkill} onValueChange={setReqSkill}>
              <SelectTrigger className="bg-slate-950 border-slate-800 flex-1">
                <SelectValue placeholder="Pick a skill..." />
              </SelectTrigger>
              <SelectContent>
                {SKILL_CATEGORIES.map((skill) => (
                  <SelectItem key={skill} value={skill}>
                    {skill}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={reqLevel.toString()} onValueChange={(v) => setReqLevel(parseInt(v))}>
              <SelectTrigger className="bg-slate-950 border-slate-800 w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Level 1 (Novice)</SelectItem>
                <SelectItem value="2">Level 2 (Competent)</SelectItem>
                <SelectItem value="3">Level 3 (Proficient)</SelectItem>
                <SelectItem value="4">Level 4 (Expert)</SelectItem>
                <SelectItem value="5">Level 5 (Master)</SelectItem>
              </SelectContent>
            </Select>

            <Button data-testid="act-add-btn" type="button" onClick={addRequirement} disabled={!reqSkill} size="sm">
              <Plus className="w-4 h-4 mr-1" />
              Add
            </Button>
          </div>
        </div>
      </div>

      {/* Skill Rewards */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Award className="w-4 h-4 text-amber-400" />
          <Label className="text-sm font-semibold text-slate-300">Skill Rewards (SP upon Completion)</Label>
        </div>
        
        <div className="space-y-2">
          {rewards.map((reward, idx) => {
            const matchingReq = requirements.find((r) => r.skill_name === reward.skill_name);
            const maxSP = matchingReq ? matchingReq.minimum_level * 2 : 1000;
            return (
              <div key={idx} className="flex items-center justify-between p-3 bg-slate-950 rounded-lg border border-slate-800">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-amber-400 border-amber-400/30">
                    {reward.skill_name}
                  </Badge>
                  <Input data-testid="act-skillrequirementsmanager-input"
                    type="number"
                    min="1"
                    max={maxSP}
                    value={reward.sp_amount}
                    onChange={(e) => updateReward(idx, e.target.value)}
                    className="bg-slate-900 border-slate-700 w-16 h-8 text-sm"
                  />
                  <span className="text-sm text-slate-400">SP (max: {maxSP})</span>
                </div>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  onClick={() => removeReward(idx)}
                  className="h-8 w-8 text-slate-500 hover:text-red-400"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            );
          })}
        </div>

        <div className="p-3 bg-slate-950/50 rounded-lg border border-dashed border-slate-700 space-y-2">
          <div className="text-xs text-slate-500 font-medium">Select skill & SP amount, then click "Add":</div>
          <div className="flex gap-2">
            <Select value={rewardSkill} onValueChange={(skill) => {
              setRewardSkill(skill);
              const matchingReq = requirements.find((r) => r.skill_name === skill);
              if (matchingReq) {
                setRewardSP(matchingReq.minimum_level);
              }
            }}>
              <SelectTrigger className="bg-slate-950 border-slate-800 flex-1">
                <SelectValue placeholder="Pick a skill..." />
              </SelectTrigger>
              <SelectContent>
                {SKILL_CATEGORIES.map((skill) => (
                  <SelectItem key={skill} value={skill}>
                    {skill}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Input data-testid="act-skillrequirementsmanager-input-1"
              type="number"
              min="1"
              max={rewardSkill && requirements.find((r) => r.skill_name === rewardSkill) ? requirements.find((r) => r.skill_name === rewardSkill).minimum_level * 2 : 1000}
              value={rewardSP}
              onChange={(e) => setRewardSP(parseInt(e.target.value))}
              className="bg-slate-950 border-slate-800 w-24"
              placeholder="SP"
            />

            <Button data-testid="act-add-btn-1" type="button" onClick={addReward} disabled={!rewardSkill || rewardSP <= 0} size="sm">
              <Plus className="w-4 h-4 mr-1" />
              Add
            </Button>
          </div>
        </div>
      </div>
      </CollapsibleContent>
    </Collapsible>
  );
}